/**
 * @file AudioService.ts
 * @role Owns the optional sound catalog, load states, mixer buses, concurrency, fades, loops,
 *       and safe playback policy. Howler access is delegated to the injected backend.
 * @owns Bus volumes, muted state, grouped best-effort preload, active playback, and disposal.
 * @reads Validated audio config, optional skin source pairs, and player Settings.
 * @neverDoes Decide outcomes. Crash on missing audio. Let a non-gameplay class import Howler.
 * @invariants 10 (config validated), audio respects mute + settings, missing audio is safe.
 * @see docs/PLAN.md (Audio System), src/core/settings/SettingsService.ts
 */
import { z } from 'zod';
import { GameError } from '@core/errors/GameError';
import { clamp01 } from '@core/math/clamp';
import type { Settings } from '@core/settings/SettingsService';
import { noopLogger, type Logger } from '@core/logging/Logger';
import {
  SOUND_IDS,
  type AudioLoadGroup,
  type AudioSourceCatalog,
  type SoundId,
  type SoundLoadState,
  type SoundPlaybackMode,
} from './SoundCatalog';

const unit = z.number().min(0).max(1);
const milliseconds = z.number().int().min(0);
const SoundIdSchema = z.enum(SOUND_IDS);
const AudioLoadGroupSchema = z
  .string()
  .refine(
    (value): value is AudioLoadGroup =>
      value === 'startup' || value === 'music' || value.startsWith('feature:'),
    'Expected startup, music, or feature:<id>',
  );
const SoundPlaybackModeSchema = z.enum(['oneShot', 'loop', 'music']);
const SoundDefinitionSchema = z.object({
  bus: z.string().min(1),
  group: AudioLoadGroupSchema,
  mode: SoundPlaybackModeSchema,
  gain: unit,
  fadeInMs: milliseconds,
  fadeOutMs: milliseconds,
  maxInstances: z.number().int().positive(),
  retriggerMs: milliseconds,
  exclusive: z.boolean(),
  stream: z.boolean(),
});

export const AudioConfigSchema = z
  .object({
    buses: z.array(z.string().min(1)).min(1),
    defaultVolumes: z.record(z.string(), unit),
    loadTimeoutMs: z.number().int().positive(),
    sounds: z.record(SoundIdSchema, SoundDefinitionSchema),
  })
  .superRefine((value, context) => {
    const buses = new Set(value.buses);
    for (const bus of Object.keys(value.defaultVolumes)) {
      if (!buses.has(bus)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `defaultVolumes references unknown bus "${bus}"`,
          path: ['defaultVolumes', bus],
        });
      }
    }
    for (const soundId of SOUND_IDS) {
      const definition = value.sounds[soundId];
      if (!definition) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Missing sound definition "${soundId}"`,
          path: ['sounds', soundId],
        });
        continue;
      }
      if (!buses.has(definition.bus)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Sound "${soundId}" references unknown bus "${definition.bus}"`,
          path: ['sounds', soundId, 'bus'],
        });
      }
      if (definition.mode === 'music' && definition.group !== 'music') {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Music sound "${soundId}" must use the music load group`,
          path: ['sounds', soundId, 'group'],
        });
      }
    }
  });
export type AudioConfig = z.infer<typeof AudioConfigSchema>;
export type SoundDefinition = z.infer<typeof SoundDefinitionSchema>;

export function validateAudioConfig(raw: unknown): AudioConfig {
  const parsed = AudioConfigSchema.safeParse(raw);
  if (!parsed.success) {
    throw new GameError('CONFIG_LOAD_FAILED', `Invalid audio config: ${parsed.error.message}`, {
      cause: parsed.error,
    });
  }
  return parsed.data;
}

/** A playable sound. The default backend wraps Howler; tests inject a fake. */
export interface SoundHandle {
  readonly selectedFormat: string | undefined;
  fadeTo(volume: number, durationMs: number, playbackId?: number): void;
  fadeOutAndStop(durationMs: number, playbackId?: number): void;
  play(): number | undefined;
  setRate(rate: number, playbackId?: number): void;
  setVolume(volume: number, playbackId?: number): void;
  stop(playbackId?: number): void;
  unload(): void;
}

/** Creates SoundHandles from sources. Swappable so audio is testable without Howler/WebAudio. */
export interface SoundCreateOptions {
  src: readonly string[];
  loop: boolean;
  html5: boolean;
  pool: number;
  onLoad: () => void;
  onLoadError: () => void;
  onEnd: (playbackId: number) => void;
}

export interface AudioBackend {
  create(options: SoundCreateOptions): SoundHandle;
  unlock?(): void;
}

interface SoundRuntime {
  definition: SoundDefinition;
  state: SoundLoadState;
  handle?: SoundHandle;
  loadPromise?: Promise<SoundLoadState>;
  loadGeneration: number;
  activeIds: number[];
  lastPlayedAtMs: number;
}

export interface PreloadSummary {
  ready: SoundId[];
  unavailable: SoundId[];
  failed: SoundId[];
}

export interface PlayOptions {
  rate?: number;
}

export interface SoundDebugState {
  id: SoundId;
  state: SoundLoadState;
  group: AudioLoadGroup;
  mode: SoundPlaybackMode;
  bus: string;
  effectiveVolume: number;
  activeInstances: number;
  selectedFormat?: string;
}

export class AudioService {
  private readonly busVolume = new Map<string, number>();
  private readonly sounds = new Map<SoundId, SoundRuntime>();
  private muted = false;
  private requestedMusic: SoundId | undefined;
  private disposed = false;

  constructor(
    private readonly backend: AudioBackend | undefined,
    raw: unknown,
    private readonly sourceCatalog: AudioSourceCatalog = {},
    private readonly logger: Logger = noopLogger,
    private readonly now: () => number = () => performance.now(),
  ) {
    const config = validateAudioConfig(raw);
    for (const bus of config.buses) {
      this.busVolume.set(bus, config.defaultVolumes[bus] ?? 1);
    }
    this.loadTimeoutMs = config.loadTimeoutMs;
    for (const soundId of SOUND_IDS) {
      const definition = config.sounds[soundId];
      if (!definition) continue;
      this.sounds.set(soundId, {
        definition,
        state: sourceCatalog[soundId] ? 'idle' : 'unavailable',
        loadGeneration: 0,
        activeIds: [],
        lastPlayedAtMs: Number.NEGATIVE_INFINITY,
      });
    }
  }

  private readonly loadTimeoutMs: number;

  /** Start loading every installed cue in a group. Never rejects or waits on unavailable cues. */
  async preloadGroup(group: AudioLoadGroup): Promise<PreloadSummary> {
    const members = SOUND_IDS.filter(
      (soundId) => this.sounds.get(soundId)?.definition.group === group,
    );
    await Promise.all(members.map((soundId) => this.ensureLoaded(soundId)));
    return {
      ready: members.filter((soundId) => this.state(soundId) === 'ready'),
      unavailable: members.filter((soundId) => this.state(soundId) === 'unavailable'),
      failed: members.filter((soundId) => this.state(soundId) === 'failed'),
    };
  }

  state(soundId: SoundId): SoundLoadState {
    return this.sounds.get(soundId)?.state ?? 'unavailable';
  }

  isAvailable(soundId: SoundId): boolean {
    return this.sourceCatalog[soundId] !== undefined;
  }

  /**
   * Play only a ready cue. Loading SFX never queue, so a time-sensitive cue cannot arrive after
   * its matching animation. Music uses requestMusic(), which is intentionally stateful.
   */
  play(soundId: SoundId, options: PlayOptions = {}): boolean {
    if (this.disposed) return false;
    const runtime = this.sounds.get(soundId);
    if (!runtime || runtime.state !== 'ready' || !runtime.handle) return false;
    const nowMs = this.now();
    if (nowMs - runtime.lastPlayedAtMs < runtime.definition.retriggerMs) return false;

    if (runtime.definition.exclusive) this.stopBus(runtime.definition.bus, soundId);
    if (runtime.definition.mode === 'loop' && runtime.activeIds.length > 0) {
      this.stop(soundId, 0);
    }
    while (runtime.activeIds.length >= runtime.definition.maxInstances) {
      const oldest = runtime.activeIds.shift();
      if (oldest !== undefined) runtime.handle.stop(oldest);
    }

    try {
      const targetVolume = this.soundVolume(runtime.definition);
      runtime.handle.setVolume(targetVolume);
      const playbackId = runtime.handle.play();
      if (playbackId === undefined) return false;
      runtime.lastPlayedAtMs = nowMs;
      runtime.activeIds.push(playbackId);
      if (options.rate !== undefined) runtime.handle.setRate(options.rate, playbackId);
      if (runtime.definition.fadeInMs > 0 && targetVolume > 0) {
        runtime.handle.setVolume(0, playbackId);
        runtime.handle.fadeTo(targetVolume, runtime.definition.fadeInMs, playbackId);
      }
      return true;
    } catch {
      return false;
    }
  }

  stop(soundId: SoundId, fadeMs?: number): void {
    const runtime = this.sounds.get(soundId);
    if (!runtime?.handle || runtime.activeIds.length === 0) return;
    const durationMs = fadeMs ?? runtime.definition.fadeOutMs;
    const activeIds = [...runtime.activeIds];
    runtime.activeIds = [];
    try {
      for (const playbackId of activeIds) {
        if (durationMs > 0) runtime.handle.fadeOutAndStop(durationMs, playbackId);
        else runtime.handle.stop(playbackId);
      }
    } catch {
      // Audio cleanup is best-effort and never affects gameplay.
    }
  }

  setBusVolume(bus: string, volume: number): void {
    if (!this.busVolume.has(bus)) return;
    this.busVolume.set(bus, clamp01(volume));
    this.updateActiveVolumes();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.updateActiveVolumes();
  }

  /** Drive the mixer from player settings: music -> music bus, sfx -> the sfx family, mute. */
  applySettings(settings: Settings): void {
    this.setBusVolume('music', settings.musicVolume);
    for (const bus of ['sfx', 'ui', 'reel', 'win']) this.setBusVolume(bus, settings.sfxVolume);
    this.setMuted(settings.muted);
  }

  /** Resume the audio context after the first user gesture (browsers gate autoplay). */
  unlock(): void {
    try {
      this.backend?.unlock?.();
    } catch {
      // Unlock is best-effort.
    }
  }

  /** Effective 0..1 volume for a bus: master * bus, or 0 when muted. Exposed for tests. */
  effectiveVolume(bus: string): number {
    if (this.muted) return 0;
    return (this.busVolume.get('master') ?? 1) * (this.busVolume.get(bus) ?? 1);
  }

  stopGroup(group: AudioLoadGroup): void {
    for (const soundId of SOUND_IDS) {
      if (this.sounds.get(soundId)?.definition.group === group) this.stop(soundId);
    }
  }

  /**
   * Stateful music is the one category allowed to begin after loading: the desired track remains
   * current while loading, unlike one-shot SFX whose timing window expires immediately.
   */
  requestMusic(soundId: SoundId | undefined): void {
    if (this.requestedMusic && this.requestedMusic !== soundId) this.stop(this.requestedMusic);
    this.requestedMusic = soundId;
    if (!soundId) return;
    const runtime = this.sounds.get(soundId);
    if (!runtime || runtime.definition.mode !== 'music') return;
    void this.ensureLoaded(soundId).then((state) => {
      if (state === 'ready' && this.requestedMusic === soundId) this.play(soundId);
    });
  }

  debugSnapshot(): SoundDebugState[] {
    return SOUND_IDS.flatMap((soundId) => {
      const runtime = this.sounds.get(soundId);
      if (!runtime) return [];
      const state: SoundDebugState = {
        id: soundId,
        state: runtime.state,
        group: runtime.definition.group,
        mode: runtime.definition.mode,
        bus: runtime.definition.bus,
        effectiveVolume: this.soundVolume(runtime.definition),
        activeInstances: runtime.activeIds.length,
      };
      if (runtime.handle?.selectedFormat) state.selectedFormat = runtime.handle.selectedFormat;
      return [state];
    });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.requestedMusic = undefined;
    for (const runtime of this.sounds.values()) {
      runtime.loadGeneration += 1;
      runtime.activeIds = [];
      try {
        runtime.handle?.stop();
        runtime.handle?.unload();
      } catch {
        // Disposal is best-effort.
      }
      delete runtime.handle;
    }
  }

  private ensureLoaded(soundId: SoundId): Promise<SoundLoadState> {
    const runtime = this.sounds.get(soundId);
    if (!runtime || runtime.state === 'unavailable' || this.disposed) {
      return Promise.resolve('unavailable');
    }
    if (runtime.state === 'ready' || runtime.state === 'failed') {
      return Promise.resolve(runtime.state);
    }
    if (runtime.loadPromise) return runtime.loadPromise;
    const sources = this.sourceCatalog[soundId];
    const backend = this.backend;
    if (!sources || !backend) {
      runtime.state = sources ? 'failed' : 'unavailable';
      return Promise.resolve(runtime.state);
    }

    runtime.state = 'loading';
    const generation = ++runtime.loadGeneration;
    runtime.loadPromise = new Promise<SoundLoadState>((resolve) => {
      let settled = false;
      const settle = (state: Extract<SoundLoadState, 'ready' | 'failed'>): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        if (runtime.loadGeneration !== generation || this.disposed) {
          resolve('failed');
          return;
        }
        runtime.state = state;
        if (state === 'failed') {
          this.logger.warn(`[audio] failed to preload "${soundId}"; cue disabled`);
        }
        resolve(state);
      };
      const timeoutId = setTimeout(() => {
        try {
          runtime.handle?.unload();
        } catch {
          // Ignore backend cleanup failure.
        }
        delete runtime.handle;
        settle('failed');
      }, this.loadTimeoutMs);
      try {
        runtime.handle = backend.create({
          src: [sources.webm, sources.mp3],
          loop: runtime.definition.mode !== 'oneShot',
          html5: runtime.definition.stream,
          pool: runtime.definition.maxInstances,
          onLoad: () => settle('ready'),
          onLoadError: () => settle('failed'),
          onEnd: (playbackId) => {
            if (runtime.definition.mode === 'oneShot') {
              runtime.activeIds = runtime.activeIds.filter((id) => id !== playbackId);
            }
          },
        });
      } catch {
        settle('failed');
      }
    });
    return runtime.loadPromise;
  }

  private stopBus(bus: string, except: SoundId): void {
    for (const soundId of SOUND_IDS) {
      if (soundId === except) continue;
      if (this.sounds.get(soundId)?.definition.bus === bus) this.stop(soundId);
    }
  }

  private updateActiveVolumes(): void {
    for (const runtime of this.sounds.values()) {
      if (!runtime.handle) continue;
      const volume = this.soundVolume(runtime.definition);
      try {
        for (const playbackId of runtime.activeIds) {
          runtime.handle.setVolume(volume, playbackId);
        }
      } catch {
        // Volume changes are best-effort.
      }
    }
  }

  private soundVolume(definition: SoundDefinition): number {
    return clamp01(this.effectiveVolume(definition.bus) * definition.gain);
  }
}
